import React, { useState, useRef, useCallback } from 'react'
import { Modal, Button, Space } from 'antd'
import { ScissorOutlined } from '@ant-design/icons'
import ReactCrop from 'react-image-crop'
import 'react-image-crop/dist/ReactCrop.css'

/**
 * Modal para recortar imagen
 */
const ModalRecorte = ({
  visible,
  onCancel,
  imagenUrl,
  onConfirmarRecorte,
  recortando,
}) => {
  const [crop, setCrop] = useState({
    unit: '%',
    width: 90,
    height: 90,
    x: 5,
    y: 5,
  })
  const [completedCrop, setCompletedCrop] = useState(null)
  const imgRef = useRef(null)
  const canvasRef = useRef(null)
  const imagenLocalUrlRef = useRef(null)

  const [imagenLocalUrl, setImagenLocalUrl] = useState(null)

  // Cargar la imagen a través de fetch para evitar problemas de CORS
  React.useEffect(() => {
    // Resetear el crop cuando se abre el modal o cambia la imagen
    setCompletedCrop(null)
    setCrop({
      unit: '%',
      width: 90,
      height: 90,
      x: 5,
      y: 5,
    })

    if (imagenUrl && visible) {
      const cargarImagen = async () => {
        try {
          // Limpiar URL anterior si existe
          if (imagenLocalUrlRef.current) {
            URL.revokeObjectURL(imagenLocalUrlRef.current)
            imagenLocalUrlRef.current = null
          }

          // Cargar la imagen como blob usando fetch
          const response = await fetch(imagenUrl, {
            mode: 'cors',
            credentials: 'omit',
          })
          if (!response.ok) {
            throw new Error('Error al cargar la imagen')
          }
          const blob = await response.blob()
          const localUrl = URL.createObjectURL(blob)
          imagenLocalUrlRef.current = localUrl
          setImagenLocalUrl(localUrl)
        } catch (error) {
          console.error('Error al cargar imagen para recorte:', error)
          // Si falla, intentar usar la URL original directamente
          setImagenLocalUrl(imagenUrl)
        }
      }
      cargarImagen()
    } else {
      setImagenLocalUrl(null)
    }

    // Limpiar URL local cuando el modal se cierra o cambia la imagen
    return () => {
      if (imagenLocalUrlRef.current) {
        URL.revokeObjectURL(imagenLocalUrlRef.current)
        imagenLocalUrlRef.current = null
      }
    }
  }, [imagenUrl, visible])

  const onImageLoaded = useCallback((img) => {
    if (!img || !img.width || !img.height) {
      console.error('Error: Imagen no válida al cargar')
      return
    }
    
    imgRef.current = img
    // Establecer un recorte inicial centrado
    const { width, height } = img
    const size = Math.min(width, height) * 0.8
    
    // Validar que el tamaño sea válido
    if (isNaN(size) || size <= 0) {
      console.error('Error: Tamaño de crop inválido', { width, height, size })
      return
    }
    
    const x = (width - size) / 2
    const y = (height - size) / 2
    
    if (isNaN(x) || isNaN(y)) {
      console.error('Error: Coordenadas de crop inválidas', { x, y })
      return
    }
    
    setCrop({
      unit: 'px',
      width: size,
      height: size,
      x,
      y,
    })
  }, [])

  const onCropChange = (newCrop) => {
    setCrop(newCrop)
  }

  const onCropComplete = (crop) => {
    setCompletedCrop(crop)
  }

  const handleConfirmar = async () => {
    if (!completedCrop || !imgRef.current || !canvasRef.current) {
      onConfirmarRecorte(null)
      return
    }

    const image = imgRef.current
    const canvas = canvasRef.current
    const crop = completedCrop

    // Validar que la imagen esté cargada correctamente
    if (!image.naturalWidth || !image.naturalHeight || !image.width || !image.height) {
      console.error('Error: La imagen no está cargada correctamente')
      onConfirmarRecorte(null)
      return
    }

    // Convertir el crop a píxeles si está en porcentaje
    let cropPx = { ...crop }
    if (crop.unit === '%') {
      cropPx = {
        x: (crop.x / 100) * image.width,
        y: (crop.y / 100) * image.height,
        width: (crop.width / 100) * image.width,
        height: (crop.height / 100) * image.height,
      }
    }

    // Validar que los valores del crop sean válidos
    if (
      !cropPx.width || !cropPx.height || 
      isNaN(cropPx.x) || isNaN(cropPx.y) || 
      isNaN(cropPx.width) || isNaN(cropPx.height) ||
      cropPx.width <= 0 || cropPx.height <= 0
    ) {
      console.error('Error: Valores de crop inválidos', cropPx)
      onConfirmarRecorte(null)
      return
    }

    const scaleX = image.naturalWidth / image.width
    const scaleY = image.naturalHeight / image.height
    const ctx = canvas.getContext('2d')

    const pixelRatio = window.devicePixelRatio || 1
    const outputWidth = Math.round(cropPx.width * scaleX * pixelRatio)
    const outputHeight = Math.round(cropPx.height * scaleY * pixelRatio)
    
    canvas.width = outputWidth
    canvas.height = outputHeight

    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0)
    ctx.imageSmoothingQuality = 'high'

    const cropX = cropPx.x * scaleX
    const cropY = cropPx.y * scaleY
    const cropWidth = cropPx.width * scaleX
    const cropHeight = cropPx.height * scaleY

    // Validar que los valores calculados sean válidos
    if (
      isNaN(cropX) || isNaN(cropY) || 
      isNaN(cropWidth) || isNaN(cropHeight) ||
      cropWidth <= 0 || cropHeight <= 0
    ) {
      console.error('Error: Valores calculados inválidos', { cropX, cropY, cropWidth, cropHeight })
      onConfirmarRecorte(null)
      return
    }

    ctx.drawImage(
      image,
      cropX,
      cropY,
      cropWidth,
      cropHeight,
      0,
      0,
      cropWidth,
      cropHeight,
    )

    canvas.toBlob((blob) => {
      if (blob) {
        onConfirmarRecorte(blob)
      } else {
        console.error('Error al convertir canvas a blob')
        onConfirmarRecorte(null)
      }
    }, 'image/png', 1.0)
  }

  return (
    <Modal
      title={
        <Space>
          <ScissorOutlined />
          <span>Recortar Imagen</span>
        </Space>
      }
      open={visible}
      maskClosable={false}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel} disabled={recortando}>
          Cancelar
        </Button>,
        <Button
          key="confirm"
          type="primary"
          onClick={handleConfirmar}
          loading={recortando}
        >
          Confirmar Recorte
        </Button>,
      ]}
      width={800}
      centered
    >
      <div style={{ textAlign: 'center', marginBottom: '16px' }}>
        {imagenLocalUrl && (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <ReactCrop
              crop={crop}
              onChange={onCropChange}
              onComplete={onCropComplete}
              aspect={undefined}
              minWidth={50}
              minHeight={50}
            >
              <img
                ref={imgRef}
                src={imagenLocalUrl}
                alt="Imagen a recortar"
                crossOrigin="anonymous"
                style={{
                  maxWidth: '100%',
                  maxHeight: '500px',
                  height: 'auto',
                  width: 'auto',
                  display: 'block',
                }}
                onLoad={(e) => onImageLoaded(e.target)}
              />
            </ReactCrop>
          </div>
        )}
        {!imagenLocalUrl && imagenUrl && (
          <div style={{ padding: '20px' }}>Cargando imagen...</div>
        )}
        <canvas
          ref={canvasRef}
          style={{ display: 'none' }}
        />
      </div>
    </Modal>
  )
}

export default ModalRecorte
